// ============ CONFIGURATION CONSTANTS ============

export const ARRIVAL_THRESHOLD_METERS = 50;
export const OFF_ROUTE_THRESHOLD_METERS = 100;
export const OFF_ROUTE_COOLDOWN_MS = 60000;
export const SKIPPED_COOLDOWN_MS = 30000;
export const AUTO_ADVANCE_DELAY_MS = 3000;

// ============ DISTANCE CALCULATIONS ============

export function calculateDistance(
	lat1: number, lng1: number,
	lat2: number, lng2: number
): number {
	const R = 6371000;
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

// ============ STOP DISTANCE ============

export interface StopDistance {
	stopId: string;
	index: number;
	distance: number;
	coords: { lat: number; lng: number };
}

export function calculateDistancesToStops(
	userLat: number, userLng: number,
	stops: Array<{ id: string; lat: number; lng: number }>
): StopDistance[] {
	return stops.map((stop, index) => ({
		stopId: stop.id,
		index,
		distance: calculateDistance(userLat, userLng, stop.lat, stop.lng),
		coords: { lat: stop.lat, lng: stop.lng },
	}));
}

export function findClosestStop(distances: StopDistance[]): StopDistance | null {
	if (distances.length === 0) return null;
	return distances.reduce((min, curr) => curr.distance < min.distance ? curr : min);
}

// ============ ARRIVAL DETECTION ============

export function isWithinArrivalThreshold(
	distance: number, threshold: number = ARRIVAL_THRESHOLD_METERS
): boolean {
	return distance <= threshold;
}

// ============ POLYLINE DECODING (Google format) ============

export function decodePolyline(encoded: string): Array<{ lat: number; lng: number }> {
	const poly: Array<{ lat: number; lng: number }> = [];
	let index = 0, lat = 0, lng = 0;

	while (index < encoded.length) {
		let b: number, shift = 0, result = 0;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		lat += result & 1 ? ~(result >> 1) : result >> 1;

		shift = 0; result = 0;
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);
		lng += result & 1 ? ~(result >> 1) : result >> 1;

		poly.push({ lat: lat / 1e5, lng: lng / 1e5 });
	}
	return poly;
}

// ============ OFF-ROUTE DETECTION ============

function pointToSegmentDistance(
	point: { lat: number; lng: number },
	start: { lat: number; lng: number },
	end: { lat: number; lng: number }
): number {
	const dx = end.lng - start.lng;
	const dy = end.lat - start.lat;
	const lenSq = dx * dx + dy * dy;
	if (lenSq === 0) return calculateDistance(point.lat, point.lng, start.lat, start.lng);
	let t = ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) / lenSq;
	t = Math.max(0, Math.min(1, t));
	return calculateDistance(point.lat, point.lng, start.lat + t * dy, start.lng + t * dx);
}

export function distanceToPolyline(
	point: { lat: number; lng: number },
	polyline: Array<{ lat: number; lng: number }>
): number {
	if (polyline.length < 2) return Infinity;
	let minDistance = Infinity;
	for (let i = 0; i < polyline.length - 1; i++) {
		minDistance = Math.min(minDistance, pointToSegmentDistance(point, polyline[i], polyline[i + 1]));
	}
	return minDistance;
}

export function isOffRoute(
	userLat: number, userLng: number,
	encodedPolyline: string, threshold: number = OFF_ROUTE_THRESHOLD_METERS
): boolean {
	const polyline = decodePolyline(encodedPolyline);
	return distanceToPolyline({ lat: userLat, lng: userLng }, polyline) > threshold;
}

export function isOffRouteFromCoords(
	userLat: number, userLng: number,
	polylineCoords: Array<{ lat: number; lng: number }>,
	threshold: number = OFF_ROUTE_THRESHOLD_METERS
): boolean {
	return distanceToPolyline({ lat: userLat, lng: userLng }, polylineCoords) > threshold;
}

export function formatDistance(meters: number): string {
	if (meters < 1000) return `${Math.round(meters)}m`;
	return `${(meters / 1000).toFixed(1)}km`;
}
